import React, { useEffect, useMemo, useState } from 'react';
import './Dashboard.css';
import './SalesScoreboard.css';
import './ComprehensiveSalesScoreboard.css';
import './FIScoreboard.css';
import NoTargetsPlaceholder from './NoTargetsPlaceholder';
import DatePicker from './DatePicker';
import { getDocFee, docFeeIncome } from '../utils/docFee';
import { resolveFiProducts } from '../utils/fiProducts';
import { overUnderFor } from '../utils/overUnder';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const FI_MANAGER_ROLE = 'Finance & Insurance Manager';

// financeManagerId is nullable, so a bike deal can reach the board with nobody
// credited for it. Those deals still count toward the dealership KPIs, so they
// need a row of their own or the leaderboard's units won't add up to the units
// card above it.
const UNASSIGNED_KEY = '__unassigned__';

const SORT_OPTIONS = [
  { value: 'total', label: 'Sort: F&I Backend $' },
  { value: 'frontend', label: 'Sort: Frontend Gross' },
  { value: 'reserve', label: 'Sort: F&I Reserve' },
  { value: 'backendPerUnit', label: 'Sort: F&I Backend Per Unit' },
  { value: 'product', label: 'Sort: F&I Product Per Unit' }
];

// How the leaderboard settles a rank once the chosen sort column ties: F&I
// Backend leads, then Product, then Reserve. Product outranks Reserve because
// it's the discretionary half of the job — reserve largely follows the lender's
// grid, so two managers level on Backend are separated by the one who wrote
// more product. Only bites with two or more managers on the board; a lone F&I
// manager has nothing to be ranked against.
const RANK_CASCADE = ['fiTotal', 'fiProduct', 'fiReserve'];

const SORT_FIELD = {
  total: 'fiTotal',
  frontend: 'frontendGross',
  reserve: 'fiReserve',
  backendPerUnit: 'totalPerUnit',
  product: 'productPerUnit'
};

function abbreviateNumber(value) {
  if (value === undefined || value === null || isNaN(value)) return '0';
  const abs = Math.abs(value);
  if (abs >= 1e9) return (value / 1e9).toFixed(abs >= 1e10 ? 0 : 1) + 'B';
  if (abs >= 1e6) return (value / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + 'M';
  if (abs >= 1e3) return (value / 1e3).toFixed(abs >= 1e4 ? 0 : 1) + 'K';
  return value.toLocaleString();
}

function formatCurrency(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return '$0';
  return '$' + abbreviateNumber(Math.round(value));
}

// Whole dollars, unabbreviated. The product averages are the one place on this
// board where the last three digits carry the meaning — "$1.2K" against a
// $1,000 target says nothing about whether the target was cleared, and it reads
// inconsistently next to the sub-$1,000 averages, which never abbreviate.
function formatExactCurrency(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return '$0';
  return '$' + Math.round(value).toLocaleString();
}

function formatNumber(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return '0';
  return abbreviateNumber(Number(value));
}

function formatPercent(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return '0%';
  return `${Math.round(value)}%`;
}

function calculatePercent(actual, target) {
  if (!target || target === 0) return 0;
  return Math.round((actual / target) * 100);
}

function percentClass(pct) {
  if (pct >= 80) return 'high';
  if (pct >= 50) return 'mid';
  return 'low';
}

function getTrendIndicator(pct) {
  if (pct >= 80) return { label: 'On Track', class: 'trend-on-track' };
  if (pct >= 50) return { label: 'Caution', class: 'trend-caution' };
  return { label: 'Behind', class: 'trend-behind' };
}

function ScoreboardDropdown({ options, value, onChange }) {
  return (
    <select className="scoreboard-dropdown" value={value} onChange={e => onChange(e.target.value)}>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

// Get a monthly target value from the monthlyTargets array for a given (0-based) month.
function getMonthlyTarget(targets, field, currentMonth) {
  if (!targets || !Array.isArray(targets.monthlyTargets)) return 0;
  const monthData = targets.monthlyTargets.find(m => m.month === currentMonth + 1);
  if (!monthData) return 0;
  return Number(monthData[field]) || 0;
}

// MTD target = the selected month's target prorated to days elapsed through the
// reference date, matching how the Sales Leader Scoreboard defines month-to-date.
function getMTDTarget(targets, field, referenceDate = new Date()) {
  if (!targets || !Array.isArray(targets.monthlyTargets)) return 0;
  const ref = new Date(referenceDate);
  const refMonth = ref.getMonth();
  const monthlyTarget = getMonthlyTarget(targets, field, refMonth);
  const daysInMonth = new Date(ref.getFullYear(), refMonth + 1, 0).getDate();
  const daysElapsed = Math.min(ref.getDate(), daysInMonth);
  return Math.round(monthlyTarget * (daysElapsed / daysInMonth));
}

// Front-end gross for a set of deals: sale price less cost less any trade
// over/under, mirroring the Sales Leader Scoreboard's gross calculation.
function frontendGrossOf(deals) {
  return deals.reduce((sum, a) => {
    const price = Number(a.salesPrice) || 0;
    const cost = Number(a.totalCost) || 0;
    return sum + (price - cost - (overUnderFor(a) || 0));
  }, 0);
}

function sumField(list, field) {
  return list.reduce((sum, a) => sum + (Number(a[field]) || 0), 0);
}

function isBikeDeal(a) {
  const val = (a.motorcycleNewUsed || '').toUpperCase();
  return val === 'NEW' || val === 'USED';
}

function hasFiProduct(a) {
  return (Number(a.fiReserve) || 0) > 0 || (Number(a.fiProduct) || 0) > 0;
}

// Per-product penetration and average income for the product cards.
//
// Penetration counts deals that took the product against every bike deal —
// ESP included. The deal record carries an espEligible flag, and an earlier
// version used it to narrow ESP's denominator to eligible units; the board now
// ignores it, so every product is measured the same way and ESP's number moves
// only when ESP sales do.
//
// The average is per product sold, not per unit: ESP $1,000 means each ESP
// written averaged $1,000, and deals without one don't dilute it.
//
// `products` is the dealership's configured benchmark list, resolved from
// Targets — the bars are per-dealership settings, not constants of the board.
function computeProductStats(bikeDeals, products) {
  return products.map((product) => {
    const sold = bikeDeals.filter(a => (Number(a[product.field]) || 0) > 0);
    const total = sumField(sold, product.field);

    return {
      field: product.field,
      label: product.label,
      sold: sold.length,
      eligible: bikeDeals.length,
      total,
      penetration: {
        value: bikeDeals.length > 0 ? (sold.length / bikeDeals.length) * 100 : 0,
        target: product.penetrationTarget
      },
      average: {
        value: sold.length > 0 ? total / sold.length : 0,
        target: product.averageTarget
      }
    };
  });
}

// Compute the dealership-wide F&I KPIs and the per-F&I-manager leaderboard from
// the same MTD set of deals, so the two sections never disagree.
function computeFiScoreboard(activities, employees, targets, selectedDate) {
  const refDate = new Date(selectedDate);
  const month = refDate.getMonth();
  const year = refDate.getFullYear();
  const refDay = refDate.getDate();

  const getUTCDateComponents = (dateStr) => {
    const d = new Date(dateStr);
    return { year: d.getUTCFullYear(), month: d.getUTCMonth(), date: d.getUTCDate() };
  };

  const monthlyActivities = (activities || []).filter((a) => {
    const c = getUTCDateComponents(a.saleDate);
    return c.year === year && c.month === month && c.date <= refDay;
  });

  const bikeDeals = monthlyActivities.filter(isBikeDeal);
  const docFeeSetting = getDocFee(targets);

  const units = bikeDeals.length;
  const frontendGross = frontendGrossOf(bikeDeals);
  const fiReserve = sumField(bikeDeals, 'fiReserve');
  const fiProduct = sumField(bikeDeals, 'fiProduct');
  const fiDocFee = docFeeIncome(bikeDeals, docFeeSetting);
  const fiTotal = fiReserve + fiProduct + fiDocFee;
  const dealsWithProduct = bikeDeals.filter(hasFiProduct).length;

  const newUnitsMTD = getMTDTarget(targets, 'newUnits', refDate);
  const usedUnitsMTD = getMTDTarget(targets, 'usedUnits', refDate);
  const unitsMTD = newUnitsMTD + usedUnitsMTD;
  const newGrossMTD = getMTDTarget(targets, 'newGrossProfit', refDate);
  const usedGrossMTD = getMTDTarget(targets, 'usedGrossProfit', refDate);
  const frontendMTD = newGrossMTD + usedGrossMTD;
  // Back End GP target maps to F&I Reserve; F&I GP target maps to F&I Product
  // (same mapping as the Sales Performance dashboard and Sales Leader Scoreboard).
  const backEndGrossMTD = getMTDTarget(targets, 'backEndGrossProfit', refDate);
  const fiGrossMTD = getMTDTarget(targets, 'fiGrossProfit', refDate);
  const fiCombinedMTD = backEndGrossMTD + fiGrossMTD;

  const metrics = {
    units: { value: units, target: unitsMTD },
    frontendGross: { value: frontendGross, target: frontendMTD },
    fiReserve: { value: fiReserve, target: backEndGrossMTD },
    fiProduct: { value: fiProduct, target: fiGrossMTD },
    fiTotal: { value: fiTotal, target: fiCombinedMTD },
    fiPerUnit: {
      value: units > 0 ? fiTotal / units : 0,
      target: unitsMTD > 0 ? fiCombinedMTD / unitsMTD : 0
    },
    frontendPerUnit: {
      value: units > 0 ? frontendGross / units : 0,
      target: unitsMTD > 0 ? frontendMTD / unitsMTD : 0
    },
    reservePerUnit: {
      value: units > 0 ? fiReserve / units : 0,
      target: unitsMTD > 0 ? backEndGrossMTD / unitsMTD : 0
    },
    productPerUnit: {
      value: units > 0 ? fiProduct / units : 0,
      target: unitsMTD > 0 ? fiGrossMTD / unitsMTD : 0
    },
    penetration: {
      value: units > 0 ? Math.round((dealsWithProduct / units) * 100) : 0,
      deals: dealsWithProduct,
      totalDeals: units
    }
  };

  // The same F&I dollars measured against the motorcycles actually delivered
  // rather than the motorcycles the plan called for: take the plan's per-unit
  // rate and scale it by units sold. This takes sales volume out of the read —
  // miss the unit target and F&I still shows honestly on the deals it got.
  //
  // The percentage this produces is identical to the per-unit cards' (the units
  // cancel), so these cards are the same measurement stated in dollars, where
  // the gap to target is legible as money rather than as an average.
  const perUnitTarget = (monthTarget) => (unitsMTD > 0 ? monthTarget / unitsMTD : 0);
  const soldMetric = (actual, monthTarget) => ({
    value: actual,
    target: perUnitTarget(monthTarget) * units,
    perUnit: units > 0 ? actual / units : 0,
    perUnitTarget: perUnitTarget(monthTarget)
  });

  const sold = {
    units,
    frontendGross: soldMetric(frontendGross, frontendMTD),
    fiReserve: soldMetric(fiReserve, backEndGrossMTD),
    fiProduct: soldMetric(fiProduct, fiGrossMTD),
    fiTotal: soldMetric(fiTotal, fiCombinedMTD)
  };

  // Seed the leaderboard from the active F&I roster so someone with zero deals
  // this period still shows up — that's what lets a lone F&I manager fall into
  // the single-person spotlight instead of an empty table.
  const roster = (employees || []).filter(e => Array.isArray(e.roles) && e.roles.includes(FI_MANAGER_ROLE));
  const groups = new Map();
  roster.forEach(e => groups.set(e.id, { employeeId: e.id, name: e.name, activities: [] }));

  bikeDeals.forEach((a) => {
    const key = a.financeManagerId || UNASSIGNED_KEY;
    if (!groups.has(key)) {
      // A deal written by someone off the current roster (departed employee, or
      // a plain name imported from a spreadsheet) still counts — just under
      // whatever name is on the deal, so nothing silently disappears. Same for a
      // deal with no F&I manager on it at all, which lands under Unassigned.
      groups.set(key, {
        employeeId: key,
        name: key === UNASSIGNED_KEY ? 'Unassigned' : (a.financeManagerName || key),
        unassigned: key === UNASSIGNED_KEY,
        activities: []
      });
    }
    groups.get(key).activities.push(a);
  });

  const leaderboard = Array.from(groups.values()).map((g) => {
    const gUnits = g.activities.length;
    const gNewUnits = g.activities.filter(a => (a.motorcycleNewUsed || '').toUpperCase() === 'NEW').length;
    const gFrontend = frontendGrossOf(g.activities);
    const gReserve = sumField(g.activities, 'fiReserve');
    const gProduct = sumField(g.activities, 'fiProduct');
    const gDocFee = docFeeIncome(g.activities, docFeeSetting);
    const gTotal = gReserve + gProduct + gDocFee;
    const gWithProduct = g.activities.filter(hasFiProduct).length;

    return {
      employeeId: g.employeeId,
      name: g.name,
      unassigned: !!g.unassigned,
      units: gUnits,
      newUnits: gNewUnits,
      usedUnits: gUnits - gNewUnits,
      frontendGross: gFrontend,
      fiReserve: gReserve,
      fiProduct: gProduct,
      fiTotal: gTotal,
      productPerUnit: gUnits > 0 ? gProduct / gUnits : 0,
      totalPerUnit: gUnits > 0 ? gTotal / gUnits : 0,
      penetration: gUnits > 0 ? Math.round((gWithProduct / gUnits) * 100) : 0
    };
  });

  return {
    metrics,
    sold,
    leaderboard,
    products: computeProductStats(bikeDeals, resolveFiProducts(targets))
  };
}

export default function FIScoreboard({ user, managedOwner } = {}) {
  const [dealerships, setDealerships] = useState([]);
  const [selectedDealership, setSelectedDealership] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [metrics, setMetrics] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [products, setProducts] = useState([]);
  const [sold, setSold] = useState(null);
  const [sortBy, setSortBy] = useState('total');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [hasTargets, setHasTargets] = useState(true);

  useEffect(() => {
    async function fetchDealerships() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const isOwnerView = user?.role === 'OWNER' || managedOwner;
        let url = isOwnerView ? `${API_URL}/api/owners/dealerships` : `${API_URL}/api/dealerships`;
        if (managedOwner && isOwnerView) {
          url += `?ownerId=${managedOwner.id}`;
        }
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error('Failed to load dealerships');
        const json = await res.json();
        setDealerships(json.dealerships || []);
        if (json.dealerships && json.dealerships.length > 0) {
          setSelectedDealership(json.dealerships[0].id);
        }
      } catch (err) {
        setError(err.message || 'Failed to load dealerships');
      } finally {
        setLoading(false);
      }
    }
    fetchDealerships();
  }, [user, managedOwner]);

  const selectedDealershipObj = useMemo(
    () => dealerships.find(d => d.id === selectedDealership) || null,
    [dealerships, selectedDealership]
  );

  useEffect(() => {
    if (!selectedDealership) return;
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [salesRes, targetsRes, employeesRes] = await Promise.all([
          fetch(`${API_URL}/api/dealerships/${selectedDealership}/sales-activities`, { headers }),
          fetch(`${API_URL}/api/targets/${selectedDealership}`, { headers }),
          fetch(`${API_URL}/api/dealerships/${selectedDealership}/employees`, { headers })
        ]);

        const salesData = salesRes.ok ? await salesRes.json() : { activities: [] };
        const targetsData = targetsRes.ok ? await targetsRes.json() : { frontEnd: {} };
        const employeesData = employeesRes.ok ? await employeesRes.json() : { employees: [] };

        const dealership = dealerships.find(d => d.id === selectedDealership);

        // When the dealership disables "always show F&I", unclosed deals don't
        // count toward any report figures until they're marked closed/committed.
        const visibleActivities = (dealership?.showUnclosedFI !== false)
          ? (salesData.activities || [])
          : (salesData.activities || []).filter((a) => a.closed);

        const {
          metrics: computedMetrics,
          sold: computedSold,
          leaderboard: computedLeaderboard,
          products: computedProducts
        } = computeFiScoreboard(
          visibleActivities,
          employeesData.employees || [],
          targetsData.frontEnd || {},
          selectedDate
        );

        setMetrics(computedMetrics);
        setSold(computedSold);
        setLeaderboard(computedLeaderboard);
        setProducts(computedProducts);
        setHasTargets(Array.isArray(targetsData.frontEnd?.monthlyTargets) && targetsData.frontEnd.monthlyTargets.length > 0);
      } catch (err) {
        setError(err.message || 'Failed to load F&I scoreboard');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    // eslint-disable-next-line
  }, [selectedDealership, selectedDate, dealerships]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(!!document.fullscreenElement || !!document.webkitFullscreenElement || !!document.mozFullScreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleFullscreen = () => {
    const el = document.querySelector('.fi-scoreboard');
    if (!fullscreen && el) {
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
      else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
    }
  };

  if (loading) return <div className="scoreboard-loading">Loading...</div>;
  if (error) return <div className="scoreboard-error">{error}</div>;
  if (!dealerships.length) return <div className="scoreboard-error">No dealerships found.</div>;
  if (!metrics) return <div className="scoreboard-loading">Loading metrics...</div>;
  if (!hasTargets) return <NoTargetsPlaceholder />;

  const m = metrics;

  const renderSummaryCard = (label, metric, formatter = (v) => v) => {
    const pct = calculatePercent(metric.value, metric.target);
    const trend = getTrendIndicator(pct);
    return (
      <div className="dashboard-summary-card">
        <div className="dashboard-card-label">{label}</div>
        <div className="dashboard-card-value">{formatter(metric.value)}</div>
        <div className="dashboard-card-target">Target: {formatter(metric.target)}</div>
        <div className={`dashboard-card-percent ${percentClass(pct)}`}>{pct}%</div>
        <div className="kpi-meter-bar">
          <div className={`kpi-meter-fill ${percentClass(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }}></div>
        </div>
        <div className={`trend-indicator ${trend.class}`}>{trend.label}</div>
      </div>
    );
  };

  // A dollar total measured against the plan's per-unit rate scaled to units
  // sold. The per-unit line under each figure is what the comparison actually
  // rests on, so it's shown rather than left implicit.
  const renderSoldCard = (label, metric) => {
    const pct = calculatePercent(metric.value, metric.target);
    const trend = getTrendIndicator(pct);
    return (
      <div className="dashboard-summary-card">
        <div className="dashboard-card-label">{label}</div>
        <div className="dashboard-card-value">{formatCurrency(metric.value)}</div>
        <div className="dashboard-card-sub">{formatExactCurrency(metric.perUnit)} / unit</div>
        <div className="dashboard-card-target">
          Target: {formatCurrency(metric.target)} · {formatExactCurrency(metric.perUnitTarget)} / unit
        </div>
        <div className={`dashboard-card-percent ${percentClass(pct)}`}>{pct}%</div>
        <div className="kpi-meter-bar">
          <div className={`kpi-meter-fill ${percentClass(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }}></div>
        </div>
        <div className={`trend-indicator ${trend.class}`}>{trend.label}</div>
      </div>
    );
  };

  // Penetration has no target — like Avg Over/Under on the Sales Leader
  // Scoreboard, it's a discipline read, not a goal. It sits in the units-sold
  // band because its denominator is the units actually delivered.
  const renderPenetrationCard = (metric) => (
    <div className="dashboard-summary-card">
      <div className="dashboard-card-label">F&I Penetration</div>
      <div className="dashboard-card-value">{formatPercent(metric.value)}</div>
      <div className="dashboard-card-sub">
        {metric.totalDeals === 0 ? '—' : `${metric.deals} of ${metric.totalDeals} deals`}
      </div>
      <div className="dashboard-card-target">No target — discipline read</div>
    </div>
  );

  const sortField = SORT_FIELD[sortBy] || 'fiTotal';
  const sortedLeaderboard = [...leaderboard].sort((a, b) => {
    // Unassigned is a bucket, not a competitor: it sits below everyone under
    // every sort so it never takes a rank off someone who earned one.
    if (a.unassigned !== b.unassigned) return a.unassigned ? 1 : -1;
    if (b[sortField] !== a[sortField]) return b[sortField] - a[sortField];
    for (const field of RANK_CASCADE) {
      if (b[field] !== a[field]) return b[field] - a[field];
    }
    if (b.units !== a.units) return b.units - a.units;
    return a.name.localeCompare(b.name);
  });

  // One card per F&I product: penetration over its eligible deals and the
  // average income per one sold, each against its own target.
  const renderProductCard = (product) => {
    const penPct = calculatePercent(product.penetration.value, product.penetration.target);
    const avgPct = calculatePercent(product.average.value, product.average.target);
    const trend = getTrendIndicator(Math.min(penPct, avgPct));

    const renderMeasure = (label, actual, target, formatter, pct) => (
      <div className="fi-product-measure">
        <div className="fi-product-measure-head">
          <span className="fi-product-measure-label">{label}</span>
          <span className={`fi-product-measure-value ${percentClass(pct)}`}>{formatter(actual)}</span>
        </div>
        <div className="kpi-meter-bar">
          <div className={`kpi-meter-fill ${percentClass(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }}></div>
        </div>
        <div className="fi-product-measure-target">Target: {formatter(target)}</div>
      </div>
    );

    return (
      <div className="fi-product-card" key={product.field}>
        <div className="fi-product-head">
          <span className="fi-product-name">{product.label}</span>
          <span className={`trend-indicator ${trend.class}`}>{trend.label}</span>
        </div>
        {renderMeasure('Penetration', product.penetration.value, product.penetration.target, formatPercent, penPct)}
        {renderMeasure('Avg / Sold', product.average.value, product.average.target, formatExactCurrency, avgPct)}
        <div className="fi-product-footnote">
          {product.eligible === 0
            ? '—'
            : `${product.sold} of ${product.eligible} deals · ${formatCurrency(product.total)} total`}
        </div>
      </div>
    );
  };

  return (
    // The fullscreen class is driven off React state rather than the :fullscreen
    // pseudo-class: the vendor-prefixed variants can't be combined into one
    // selector list, since a browser that doesn't recognise one of them throws
    // the whole rule away.
    <div className={`fi-scoreboard${fullscreen ? ' fi-fullscreen' : ''}`}>
      <div className="scoreboard-header">
        <h1><span style={{ color: '#ff8c00', fontSize: '1.2em' }}>{selectedDealershipObj?.name || ''}: F&amp;I Scoreboard</span></h1>
        <div className="scoreboard-controls">
          <ScoreboardDropdown
            options={dealerships.map(d => ({ value: d.id, label: d.name }))}
            value={selectedDealership}
            onChange={setSelectedDealership}
          />
          <DatePicker
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            label=""
          />
          <button className="fullscreen-btn" onClick={handleFullscreen}>
            {fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </button>
        </div>
      </div>

      <div className="comprehensive-content">
        <div className="fi-band">
          <h3 className="fi-band-title">
            Performance vs Target (MTD)
            <span className="fi-band-note">against plan, prorated to today</span>
          </h3>
          <div className="dashboard-summary-row">
          {renderSummaryCard('Units Sold', m.units, formatNumber)}
          {renderSummaryCard('Frontend Gross', m.frontendGross, formatCurrency)}
          {renderSummaryCard('F&I Backend', m.fiTotal, formatCurrency)}
          {renderSummaryCard('F&I Reserve', m.fiReserve, formatCurrency)}
          {renderSummaryCard('F&I Product', m.fiProduct, formatCurrency)}
          {renderSummaryCard('F&I Backend Per Unit', m.fiPerUnit, formatCurrency)}
          </div>
        </div>

        {sortedLeaderboard.length === 0 ? (
          <div className="leaderboard-section">
            <h3 className="leaderboard-title">F&amp;I Leaderboard (MTD)</h3>
            <p className="fi-empty-roster">
              No Finance &amp; Insurance Manager is set up for this dealership yet. Add one under User Management to start tracking F&amp;I performance.
            </p>
          </div>
        ) : (
          <div className="leaderboard-section">
            <div className="fi-leaderboard-header">
              <h3 className="leaderboard-title">F&amp;I Leaderboard (MTD)</h3>
              <ScoreboardDropdown options={SORT_OPTIONS} value={sortBy} onChange={setSortBy} />
            </div>
            <div className="leaderboard-table-wrapper">
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Name</th>
                    <th>Units</th>
                    <th>Frontend</th>
                    <th>Backend</th>
                    <th>Reserve</th>
                    <th>Product</th>
                    <th>F&amp;I Backend Per Unit</th>
                    <th>F&amp;I Product Per Unit</th>
                    <th>Penetration</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLeaderboard.map((performer, idx) => (
                    <tr
                      key={performer.employeeId || idx}
                      className={!performer.unassigned && idx < 3 ? `top-${idx + 1}` : ''}
                    >
                      <td className="rank-cell">{performer.unassigned ? '—' : `#${idx + 1}`}</td>
                      <td className="name-cell">{performer.name || 'Unknown'}</td>
                      <td>{formatNumber(performer.units)}</td>
                      <td>{formatCurrency(performer.frontendGross)}</td>
                      <td>{formatCurrency(performer.fiTotal)}</td>
                      <td>{formatCurrency(performer.fiReserve)}</td>
                      <td>{formatCurrency(performer.fiProduct)}</td>
                      <td>{formatCurrency(performer.totalPerUnit)}</td>
                      <td>{formatCurrency(performer.productPerUnit)}</td>
                      <td>{formatPercent(performer.penetration)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {products.length > 0 && (
          <div className="leaderboard-section fi-product-section">
            <h3 className="leaderboard-title">Product Performance (MTD)</h3>
            <div className="fi-product-grid">
              {products.map(renderProductCard)}
            </div>
          </div>
        )}

        {sold && (
          <div className="fi-band">
            <h3 className="fi-band-title">
              Performance on {formatNumber(sold.units)} Units Sold (MTD)
              <span className="fi-band-note">plan's per-unit rate, scaled to units actually delivered</span>
            </h3>
            <div className="dashboard-summary-row fi-sold-row">
              {renderSoldCard('Frontend Gross', sold.frontendGross)}
              {renderSoldCard('F&I Backend', sold.fiTotal)}
              {renderSoldCard('F&I Reserve', sold.fiReserve)}
              {renderSoldCard('F&I Product', sold.fiProduct)}
              {renderPenetrationCard(m.penetration)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
